import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimple, DownloadSimple, CircleNotch, FileCsv, CheckCircle, XCircle } from "@phosphor-icons/react";

const COLUMNS = [
  "codigo", "tipo", "nombre", "descripcion", "numero_lote",
  "fecha_importacion", "fecha_vencimiento", "cantidad_inicial",
  "costo_unitario", "costo_embalaje",
];

const TEMPLATE =
  COLUMNS.join(",") +
  "\n" +
  "ENV-010,PET,Envase 500ml PET,Botella transparente,LT-2026-0100,2026-01-15,2026-09-30,1000,0.15,2.50";

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setLoading(false);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_lotes.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Selecciona un archivo");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.upload("/lotes/import", fd);
      setResult(data);
      if (data.creados > 0) {
        toast.success(`${data.creados} lote(s) importado(s)`);
        onImported();
      }
      if (data.creados === 0) {
        toast.error("No se importó ningún lote. Revisa los errores.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">Importar lotes</DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Sube un archivo CSV o Excel (.xlsx) con tus lotes. Las columnas deben coincidir con la plantilla.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <button
            data-testid="download-template"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:underline"
          >
            <DownloadSimple size={18} weight="bold" /> Descargar plantilla CSV
          </button>

          <div className="text-xs text-[#64748B] bg-[#F1F5F9] rounded-md p-3 leading-relaxed">
            <span className="font-semibold text-[#475569]">Columnas:</span> {COLUMNS.join(", ")}.
            <br />Las columnas <b>codigo, tipo, nombre</b> definen el producto (se reutiliza si ya existe ese código).
            <br /><b>descripcion</b> y <b>fecha_vencimiento</b> son opcionales. Fechas en formato AAAA-MM-DD o DD/MM/AAAA.
          </div>

          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-[#E2E8F0] rounded-md p-8 text-center cursor-pointer hover:bg-[#F8F9FA] transition-colors"
            data-testid="import-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              data-testid="import-file-input"
              className="hidden"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setResult(null);
              }}
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-[#0F172A] font-medium">
                <FileCsv size={22} weight="bold" /> {file.name}
              </div>
            ) : (
              <div className="text-[#64748B] flex flex-col items-center gap-2">
                <UploadSimple size={28} />
                <span className="text-sm">Haz clic para seleccionar un archivo</span>
              </div>
            )}
          </div>

          {result && (
            <div className="space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#065F46]">
                <CheckCircle size={18} weight="bold" /> {result.creados} de {result.total} lotes importados
                {result.productos_creados ? ` · ${result.productos_creados} producto(s) nuevo(s)` : ""}
              </div>
              {result.errores?.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3 text-xs">
                  <div className="flex items-center gap-1 font-semibold text-[#991B1B] mb-1">
                    <XCircle size={14} weight="bold" /> {result.errores.length} fila(s) con error:
                  </div>
                  {result.errores.map((e, i) => (
                    <div key={i} className="text-[#991B1B]">Fila {e.fila}: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="import-close">
            Cerrar
          </Button>
          <Button
            onClick={submit}
            disabled={loading || !file}
            data-testid="import-submit"
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
          >
            {loading ? <CircleNotch size={18} className="animate-spin" /> : "Importar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
